import React from 'react';
import PropTypes from 'prop-types';
import Translate from '../../../../../components/Translate';
import ImagesViewer from '../../../../../components-new/general/ImagesViewer';
import VideoViewer from './VideoViewer';
import ConversationSkeleton from './ConversationSkeleton';
import ConversationHeader from './ConversationHeader';
import '../chat-css/ChatCard.css';
import MessageList from './MessageList';
import MessageInputWithRef from './MessageInput';
import {FileMapper} from '../../../../../utils/FileService';
import '../chat-css/Conversation.css';
import {convertFormBackendDate, dummyTrue, hasManagementPermission, sameDay} from '../../../../../utils/General';
import {getCompanyMessageConfig, getCustomTemplates, getTemplates} from "../../../../../services/company/api";
import config from 'config';
import {moveConversationToManagedList} from "../../../../../services/chat/api";
import {addNotification as addNotificationAction} from "../../../../../services/notification/action";
import {connect} from "react-redux";
import {emitter} from "../../../../../services/emitter";
import ForwardIcon from "../../../../../assets/icons/ForwardIcon";
import {clearSelectedConversation as clearSelectedConversationFromStore} from "../../../../../services/chat/action";
import ForumIcon from "../../../../../assets/icons/ForumIcon";
import Switch from "../../../../../components-new/form/Switch";
import BlueButton from "../../../../../components-new/buttons/BlueButton";
import DestructiveButton from "../../../../../components-new/buttons/DestructiveButton";
import ShieldUserIcon from "../../../../../assets/icons/ShieldUserIcon";
import OperatorProfileDrawer from "../../../../../components-new/general/OperatorProfileDrawer";

const DENIED_MESSAGE_CONFIG = {
    operatorCanSeeDraft: false,
    operatorCanEditMessage: false,
    operatorCanDeleteMessage: false,
    operatorCanReactToMessage: false,
    operatorCanSendVoiceMessage: false,
};

/*
 * How long the conversation is kept on screen after the minimise button is
 * pressed, so that it can be seen to close. Must match .conversation.is-closing
 * in Conversation.css.
 */
const MINIMISE_MS = 150;

class Conversation extends React.Component {
    constructor(props) {
        super(props);
        this.childRef = React.createRef();
        this.messageListRef = React.createRef();
        this.state = {
            /* the minimise button has been pressed and the pane is playing
               itself out - see MINIMISE_MS */
            closing: false,
            isImageViewerOpen: false,
            imageViewerPhotoIndex: 0,
            isVideoViewerOpen: false,
            videoAttachment: null,
            templates: [],
            sugTemplates: [],
            sugQuery: '',
            editingMessage: null,
            activeIndex: -1,
            replyTo: null,
            messageSearch: '',
            searchResults: [],
            currentSearchIndex: 0,
            silentMode: false,
            operatorProfile: null,
            messageConfig: DENIED_MESSAGE_CONFIG,
        };
    }

    /*
     * Replying and editing are alternatives, not layers.
     *
     * Both load the one field at the foot of the conversation, so holding both
     * at once would mean two shelves above it and no answer to what pressing
     * send does. Whichever was asked for last wins, and the other is dropped -
     * the same way a reply already replaced the reply before it.
     */
    setReplyTo = (message) => {
        if (message && this.state.editingMessage) {
            this.setState({editingMessage: null});
            // the field is holding the message that was being edited
            if (this.childRef.current) this.childRef.current.clearDraft();
        }

        this.setState({replyTo: message});
    }

    componentDidMount() {
        this.fetchTemplates();
        this.fetchMessageConfig();
        document.addEventListener('keydown', this.handleKeyDown);
        /* capture: the list itself stops the click from reaching here, and a
           press anywhere else should close it wherever that else is */
        document.addEventListener('mousedown', this.handleOutsideClick, true);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.conversation.id !== this.props.conversation.id) {
            this.fetchTemplates();
            this.setState({
                sugTemplates: [],
                sugQuery: '',
                editingMessage: null,
                silentMode: false,
                activeIndex: -1,
            });
        }

        const previousCompanyId = prevProps.conversation && prevProps.conversation.companyId;
        const companyId = this.props.conversation && this.props.conversation.companyId;
        const wasReadOnly = prevProps.readOnly || !prevProps.conversation.chatId;
        const isReadOnly = this.props.readOnly || !this.props.conversation.chatId;

        if (previousCompanyId !== companyId || wasReadOnly !== isReadOnly) {
            this.fetchMessageConfig();
        }
    }

    /*
     * Closes the chat, after letting it close.
     *
     * The button used to clear the conversation from the store directly, and the
     * store is what decides whether this pane or the empty screen is drawn - so a
     * full screen of conversation was replaced by an empty one between two
     * frames, with nothing to say which way it had gone.
     *
     * The store is told last. Until then the pane still has its conversation and
     * goes on rendering it normally; all that changes is a class that plays it
     * out. Nothing here is allowed to be clicked while it leaves - a press that
     * lands on a chat already closing acts on a chat that is gone.
     */
    handleMinimise = () => {
        const {clearSelectedConversationFromStore} = this.props;

        if (this.state.closing) return;

        this.setState({closing: true});

        this.minimiseTimeout = setTimeout(() => {
            if (this.unmounted) return;
            this.setState({closing: false});
            clearSelectedConversationFromStore();
        }, MINIMISE_MS);
    };

    componentWillUnmount() {
        this.unmounted = true;
        clearTimeout(this.minimiseTimeout);
        this.messageConfigRequestId = (this.messageConfigRequestId || 0) + 1;
        const {onUnMount} = this.props;
        onUnMount();
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('mousedown', this.handleOutsideClick, true);
    }

    fetchTemplates = () => {
        const {conversation, readOnly} = this.props;
        if (!readOnly) {
            if (conversation && conversation.platform !== config.platformsMap.company && conversation.companyId && conversation.status !== config.conversationStatuses.botClosed) {
                Promise.all([
                    getTemplates(conversation.companyId),
                    getCustomTemplates()
                ])
                    .then(([templates, customTemplates]) => {
                        this.setState({templates: [...templates, ...customTemplates]});
                    })
                    .catch((error) => {
                        console.error("Error fetching templates:", error);
                    });
            }
        }
    }

    fetchMessageConfig = () => {
        const {conversation, readOnly} = this.props;
        const requestId = (this.messageConfigRequestId || 0) + 1;
        this.messageConfigRequestId = requestId;

        /*
         * Not for a chat between colleagues.
         *
         * Everything this config unlocks - editing, deleting, reacting, seeing a
         * draft - is checked against the webchat platform as well, over in
         * Message.jsx, so on an internal chat the answer changes nothing and the
         * request is one round trip spent to be ignored. The templates fetch
         * above has skipped these chats for the same reason for a while.
         *
         * It also costs something visible. The reply arriving is what adds the
         * reaction strip under every message, and that is a change of height
         * under a conversation already scrolled to its end - see the comment in
         * MessageList.componentDidUpdate. Not asking is one fewer way for that
         * to happen.
         */
        const isInternalChat = conversation && conversation.platform === config.platformsMap.company;

        if (readOnly || isInternalChat || !conversation || !conversation.chatId || !conversation.companyId) {
            this.setState({messageConfig: DENIED_MESSAGE_CONFIG});
            return;
        }

        this.setState({messageConfig: DENIED_MESSAGE_CONFIG});
        getCompanyMessageConfig(conversation.companyId)
            .then(messageConfig => {
                if (this.messageConfigRequestId === requestId) {
                    this.setState({messageConfig: {...DENIED_MESSAGE_CONFIG, ...messageConfig}});
                }
            })
            .catch(() => {
                if (this.messageConfigRequestId === requestId) {
                    this.setState({messageConfig: DENIED_MESSAGE_CONFIG});
                }
            });
    }

    updateTemplates = (newTemplate) => {
        this.setState(prev => ({
            templates: [...prev.templates, newTemplate]
        }));
    }

    getAttachments = (type) => {
        const {conversation} = this.props;
        const {messages} = conversation;
        const typeMessages = messages.filter((message) => message.attachment && message.type === type);
        return typeMessages.map((message) =>
            Object.assign({}, message.attachment, {
                date: message.sentAt,
                id: message.messageId,
            })
        );
    };

    closeImageViewer = () => {
        this.setState({
            isImageViewerOpen: false,
            imageViewerPhotoIndex: 0,
        });
    };

    openImageViewer = (imageAttachment) => {
        const imageAttachments = this.getAttachments(FileMapper.image);
        const photoIndex = imageAttachments.findIndex((curImageAttachment) => curImageAttachment.url === imageAttachment.url);

        this.setState({
            isImageViewerOpen: true,
            imageViewerPhotoIndex: photoIndex,
        });
    };

    closeVideoViewer = () => {
        this.setState({
            isVideoViewerOpen: false,
        });
    };

    openVideoViewer = (videoAttachment) => {
        this.setState({
            isVideoViewerOpen: true,
            videoAttachment,
        });
    };

    /*
     * @param query what was typed to find them. It is kept so the panel can show
     *   which part of a template matched - without it every row looked like an
     *   arbitrary suggestion.
     */
    /*
     * The message being edited, held here rather than in the bubble: the field
     * that edits it lives at the foot of the conversation, and a bubble scrolled
     * out of sight cannot own the thing that is still on screen.
     */
    setEditingMessage = (message) => {
        this.setState({editingMessage: message, replyTo: null}, () => {
            if (this.childRef.current) this.childRef.current.beginEdit(message);
        });
    }

    setSugTemplates = (data, query = '') => {
        this.setState({sugTemplates: data, sugQuery: query})
    }

    callChildFunction = (text) => {
        if (this.childRef.current) {
            this.childRef.current.selectTemplate(text);
        }
    };

    showConversationHistory = (conversationInfo) => {
        const {selectConversation} = this.props; // passed by index.jsx, ConversationHistory not pass this props
        selectConversation({
            id: conversationInfo.id,
            companyId: conversationInfo.companyId,
            platform: conversationInfo.platform,
            userFirstName: conversationInfo.userFirstName,
            userLastName: conversationInfo.userLastName,
        });
    };

    /*
     * The list is offered, not imposed: it opens on two characters of a word
     * that happens to match a template, and until now the only ways out were to
     * pick one or to keep typing until nothing matched.
     */
    closeSuggestions = () => {
        if (this.state.sugTemplates.length === 0) return;
        this.setState({sugTemplates: [], sugQuery: '', activeIndex: -1});
    };

    handleOutsideClick = (e) => {
        if (this.state.sugTemplates.length === 0) return;
        // the field is not outside: typing in it is what the list responds to
        if (e.target.closest('.sug-templates, .message-input__composer')) return;
        this.closeSuggestions();
    };

    handleKeyDown = (e) => {
        const {sugTemplates} = this.state;
        const {activeIndex} = this.state;

        if (!Array.isArray(sugTemplates) || sugTemplates.length === 0) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            this.closeSuggestions();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = (activeIndex + 1) % sugTemplates.length;
            this.setState({activeIndex: nextIndex}, this.scrollToActive);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevIndex = (activeIndex - 1 + sugTemplates.length) % sugTemplates.length;
            this.setState({activeIndex: prevIndex}, this.scrollToActive);
        /*
         * Only a row the arrow keys chose. Hovering sets nothing - it is drawn
         * by CSS and means no more than "the cursor is over this" - because a
         * resting mouse would otherwise arm the row the panel reopened under,
         * and the next Enter picked that template instead of sending what the
         * operator had just finished editing.
         */
        } else if (e.key === 'Enter' && activeIndex !== -1) {
            e.preventDefault();
            const selected = sugTemplates[activeIndex];
            if (selected) {
                this.callChildFunction(selected.text);
                this.setState({activeIndex: -1});
            }
        }
    };

    scrollToActive = () => {
        const el = document.getElementById(`sug-template-${this.state.activeIndex}`);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    };

    handleMessageSearch = (e) => {
        const messageSearch = e.target.value.toLowerCase();

        if (!messageSearch) {
            this.setState({
                messageSearch: '',
                searchResults: [],
                currentSearchIndex: 0
            }, () => emitter.emmit('chatScrollToBottom'));
            return;
        }

        const searchResults = [];

        this.props.conversation.messages.forEach((msg, index) => {
            if (msg.text && msg.text.toLowerCase().includes(messageSearch)) {
                searchResults.push({id: msg.id, index});
            }
        });

        this.setState({
            messageSearch,
            searchResults,
            currentSearchIndex: 0
        }, () => {
            if (searchResults.length > 0) {
                emitter.emmit('chatScrollToSearchResult', 0)
            }
        });
    };

    handleNextSearch = () => {
        const {currentSearchIndex, searchResults} = this.state;

        if (searchResults.length === 0) return;

        const nextIndex = (currentSearchIndex + 1) % searchResults.length;
        this.setState({currentSearchIndex: nextIndex}, () => {
            emitter.emmit('chatScrollToSearchResult', nextIndex);
        });
    };

    handlePreviousSearch = () => {
        const {currentSearchIndex, searchResults} = this.state;

        if (searchResults.length === 0) return;

        const prevIndex = (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
        this.setState({currentSearchIndex: prevIndex}, () => {
            emitter.emmit('chatScrollToSearchResult', prevIndex);
        });
    };

    openOperatorProfile = (operator) => {
        if (!this.props.user || !this.props.user.authorities || !hasManagementPermission(this.props.user)) return;
        if (!operator || !operator.id || operator.bot) return;
        this.setState({
            operatorProfile: {
                id: operator.id,
                firstName: operator.firstName,
                lastName: operator.lastName,
                fullName: [operator.firstName, operator.lastName].filter(Boolean).join(' '),
                email: operator.email,
                thumbUrl: operator.thumbUrl,
            }
        });
    };

    closeOperatorProfile = () => {
        this.setState({operatorProfile: null});
    };

    render() {
        let {
            conversation,
            sendChatMessage,
            openForwardDialog,
            openContactList,
            user,
            loadOlderMessages,
            showSideBar,
            readOnly,
            hasBackButton,
            company,
            filteredMembers,
            forwardDialog,
            closeForwardDialog,
            forwardConversation,
            tariff,
            addNotification,
            hotkeys,
            clearSelectedConversationFromStore,
            fromUnmanagedList,
            isConversationHistory
        } = this.props;
        const {
            isImageViewerOpen,
            imageViewerPhotoIndex,
            isVideoViewerOpen,
            videoAttachment,
            templates,
            sugTemplates,
            messageSearch,
            searchResults,
            currentSearchIndex,
            operatorProfile,
            messageConfig,
        } = this.state;

        const {selectConversation} = this.props; // passed by index.jsx, ConversationHistory not pass this props
        const canViewOperatorProfile = user && user.authorities && hasManagementPermission(user);

        const {messages, messagesTotalNumber, loadingOlderMessages} = conversation;

        const {loading} = conversation;

        if (loading) {
            return <ConversationSkeleton/>;
        }

        if (conversation.id === null && !readOnly) {
            return <NoConversationSelectedView openContactList={openContactList}/>;
        }

        if (!conversation.chatId) { // important check
            readOnly = true
        }

        const timeline = []

        const reversedMessages = [...(messages)]

        reversedMessages.reverse()

        reversedMessages.forEach((message, index, arr) => {
            let displayTimeLine = false;
            if (index === 0) {
                displayTimeLine = messagesTotalNumber <= messages.length;
            } else {
                const prevMessage = arr[index - 1];
                displayTimeLine = !sameDay(convertFormBackendDate(message.date), convertFormBackendDate(prevMessage.date));
            }

            if (displayTimeLine) {
                timeline.push({date: message.createdAt, id: message.id});
            }
        });

        const uniqueTimeline = timeline.filter((item, index, self) =>
            index === self.findIndex(t => t.id === item.id && t.date === item.date)
        );

        const isFromChatContact = conversation.fromChatContact;

        return (
            <div className={'conversation full-height conversation--entering'
                + (this.state.closing ? ' is-closing' : '')}>
                <div className="content">

                    <div className="header-wrapper">
                        <ConversationHeader
                            fromUnmanagedList={fromUnmanagedList}
                            clearSelectedConversationFromStore={this.handleMinimise}
                            toggleMediaViewDiv={(e) => {
                                if (this.messageListRef.current && this.messageListRef.current.toggleMediaViewDiv) {
                                    this.messageListRef.current.toggleMediaViewDiv(e);
                                }
                            }}
                            showConversationHistory={this.showConversationHistory}
                            hotkeys={hotkeys}
                            tariff={tariff}
                            company={company}
                            hasBackButton={hasBackButton}
                            conversation={conversation}
                            openForwardDialog={openForwardDialog}
                            openContactList={openContactList}
                            readOnly={readOnly}
                            user={user}
                            filteredMembers={filteredMembers}
                            state={forwardDialog}
                            close={closeForwardDialog}
                            forwardConversation={forwardConversation}
                            uniqueTimeline={uniqueTimeline}
                            messageSearch={messageSearch}
                            handleMessageSearch={this.handleMessageSearch}
                            searchResults={searchResults}
                            currentSearchIndex={currentSearchIndex}
                            handleNextSearch={this.handleNextSearch}
                            handlePreviousSearch={this.handlePreviousSearch}
                            messages={messages}
                            isConversationHistory={isConversationHistory}
                        />
                    </div>
                    {/* keyed to the chat: the window keeps what it has read and
                        what has arrived since in its own state, and none of that
                        means anything in the next conversation */}
                    <div className="messages-wrapper" key={conversation.id}>
                        <MessageList
                            readOnly={readOnly}
                            ref={this.messageListRef}
                            childRef={this.childRef} // MessageInput ref
                            setReplyTo={this.setReplyTo}
                            setEditingMessage={this.setEditingMessage}
                            editingMessage={this.state.editingMessage}
                            replyTo={this.state.replyTo}
                            company={company}
                            conversation={conversation}
                            messages={messages}
                            messagesTotalNumber={messagesTotalNumber}
                            loadingOlderMessages={loadingOlderMessages}
                            openImageViewer={this.openImageViewer}
                            openVideoViewer={this.openVideoViewer}
                            loadOlderMessages={loadOlderMessages}
                            user={user}
                            templates={templates}
                            updateTemplates={this.updateTemplates}
                            openOperatorProfile={canViewOperatorProfile ? this.openOperatorProfile : undefined}
                            searchResults={searchResults}
                            currentSearchIndex={currentSearchIndex}
                            messageConfig={messageConfig}
                        />
                    </div>
                    {!readOnly && (
                        <div>

                            {/* keyed to the chat: the draft it is holding
                                belongs to one, so the footer is rebuilt when
                                another is opened - without anything to watch,
                                since a rebuild is not an arrival */}
                            <div className="message-input-wrapper" key={conversation.id}>
                                <MessageInputWithRef
                                    setReplyTo={this.setReplyTo}
                                    replyTo={this.state.replyTo}
                                    ref={this.childRef}
                                    setSugTemplates={this.setSugTemplates}
                                    sugTemplates={sugTemplates}
                                    sugQuery={this.state.sugQuery}
                                    sugActiveIndex={this.state.activeIndex}
                                    onPickTemplate={this.callChildFunction}
                                    onCloseTemplates={this.closeSuggestions}
                                    editingMessage={this.state.editingMessage}
                                    setEditingMessage={(m) => this.setState({editingMessage: m})}
                                    conversation={conversation}
                                    tariff={tariff}
                                    sendChatMessage={sendChatMessage}
                                    user={user}
                                    templates={templates}
                                    addNotification={addNotification}
                                    hotkeys={hotkeys}
                                    messageConfig={messageConfig}
                                />
                            </div>
                        </div>
                    )}

                    {readOnly && (
                        <div className="conversation-takeover-bar">
                            <div className={`conversation-silent-mode ${this.state.silentMode ? 'is-active' : ''}`}>
                                <Switch
                                    checked={this.state.silentMode}
                                    onChange={(silentMode) => this.setState({silentMode})}
                                    label={<Translate text="silentMode"/>}
                                />
                            </div>

                            {/* Take button — shown only when conversation comes from chat contact */}
                            {isFromChatContact && (
                                <BlueButton
                                    className="conversation-takeover-bar__button"
                                    width="auto"
                                    height="36px"
                                    onClick={() => {
                                        clearSelectedConversationFromStore();
                                        selectConversation(conversation.fullConversationForSelect, this.state.silentMode);
                                    }}
                                >
                                    <ForwardIcon height={18} width={18} stroke="currentColor"/>
                                    <span className="conversation-takeover-bar__label"><Translate text="take"/></span>
                                </BlueButton>
                            )}

                            <DestructiveButton
                                variant="outline"
                                width="auto"
                                height="36px"
                                styles={{flex: '1 1 240px', minWidth: 0}}
                                onClick={async () => {
                                    try {
                                        await moveConversationToManagedList(conversation.id, this.state.silentMode);
                                        clearSelectedConversationFromStore();
                                        if (conversation.fullConversationForSelect) { // from unmanaged list
                                            selectConversation(conversation.fullConversationForSelect, false, false); // without take
                                        } else {
                                            addNotification("taken", config.notificationLevels.info);
                                        }
                                    } catch (error) {
                                        if (error.response && error.response.status === 409) {
                                            addNotification('chatAlreadyInYourProcessingList', config.notificationLevels.warning);
                                            clearSelectedConversationFromStore();
                                        } else {
                                            addNotification('somethingWentWrong', config.notificationLevels.error);
                                            clearSelectedConversationFromStore();
                                        }
                                    }
                                }}
                            >
                                <ShieldUserIcon height={18} width={18} stroke="currentColor"/>
                                <span className="conversation-takeover-bar__label"><Translate
                                    text="take(adminMode)"/></span>
                            </DestructiveButton>

                        </div>
                    )}
                </div>

                {isImageViewerOpen && (
                    <ImagesViewer
                        images={this.getAttachments(FileMapper.image)}
                        photoIndex={imageViewerPhotoIndex}
                        close={this.closeImageViewer}
                    />
                )}
                {isVideoViewerOpen && <VideoViewer close={this.closeVideoViewer} video={videoAttachment}/>}
                {canViewOperatorProfile && (
                    <OperatorProfileDrawer
                        open={!!operatorProfile}
                        onClose={this.closeOperatorProfile}
                        operator={operatorProfile}
                        parentCompanyId={company && company.id}
                    />
                )}
            </div>
        );
    }
}

/*
 * Nothing open yet. Drawn like the empty chat list beside it - a small icon in
 * a tinted box, a line of prompt under it - rather than a 120px brand-blue glyph
 * with 22px brand-blue text, which was the loudest thing on a screen that has
 * nothing on it.
 */
const NoConversationSelectedView = ({openContactList}) => (
    <div className="start-conversation full-height chat-empty">
        <span className="chat-empty__icon">
            <ForumIcon width={34} height={34}/>
        </span>
        <div className="lg-hidden-down">
            <div className="chat-empty__title">
                <Translate text="selectUserToStartChat"/>
            </div>
        </div>
        <div className="lg-hidden-up">
            <button
                type="button"
                className="no-style-button temporary-drawer-toggle chat-empty__title"
                onClick={openContactList}
            >
                <Translate text="selectUserToStartChat"/>
            </button>
        </div>
    </div>
);

NoConversationSelectedView.propTypes = {
    openContactList: PropTypes.func.isRequired,
};

Conversation.propTypes = {
    user: PropTypes.object.isRequired,
    conversation: PropTypes.object,
    loadOlderMessages: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    showSideBar: PropTypes.bool,
    hasBackButton: PropTypes.bool,
    sendChatMessage: PropTypes.func,
    endConversation: PropTypes.func,
    openForwardDialog: PropTypes.func,
    openContactList: PropTypes.func,
    onUnMount: PropTypes.func,
};

Conversation.defaultProps = {
    readOnly: false,
    showSideBar: true,
    sendChatMessage: dummyTrue,
    endConversation: dummyTrue,
    openForwardDialog: dummyTrue,
    openContactList: dummyTrue,
    onUnMount: dummyTrue,
};

const mapDispatchToProps = dispatch => ({
    addNotification: (message, level, config) => {
        dispatch(addNotificationAction(message, level, config));
    },
    clearSelectedConversationFromStore: () => {
        dispatch(clearSelectedConversationFromStore());
    },
});

export default connect(null, mapDispatchToProps)(Conversation);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/Conversation.jsx